import { useCallback, useState } from "react";
import { Button, TextInput } from "risa-oasis-ui_v2";
import { PlusCircle } from "../../../../../shared/svg/plusCircle";
import SearchBar from "../../../searchBar/searchBar";
import { DX_CODE_ROW_TYPE_OPTIONS } from "../dxCodeRowTagOptions";
import { DxCodeEntryInput } from "../editDxCodeSidePanel";
import DxCodeRowTagFilterBar from "./DxCodeRowTagFilterBar";

interface AddCodeSectionProps {
  onAddCode: (code: DxCodeEntryInput) => void;
  onSearchChange: (searchTerm: string) => void;
  searchTerm: string;
}

const AddCodeSection = ({
  onAddCode,
  onSearchChange,
  searchTerm,
}: AddCodeSectionProps) => {
  const [showAddCodeSection, setShowAddCodeSection] = useState<boolean>(false);
  const [newDxCode, setNewDxCode] = useState<string>("");
  const [newPriority, setNewPriority] = useState<string>("");
  const [newType, setNewType] = useState<string[]>([]);

  const handleAddCodeClick = useCallback(() => {
    setShowAddCodeSection(true);
  }, []);

  const handleCancelAddCode = useCallback(() => {
    setShowAddCodeSection(false);
    setNewDxCode("");
    setNewPriority("");
    setNewType([]);
  }, []);

  const handleAddCode = useCallback(() => {
    const trimmedCode = newDxCode.trim();
    const trimmedPriority = newPriority.trim();
    let priority: number | undefined = undefined;
    if (trimmedPriority && trimmedPriority !== "-") {
      const parsedPriority = parseInt(trimmedPriority, 10);
      priority = isNaN(parsedPriority) ? undefined : parsedPriority;
    }

    if (trimmedCode) {
      onAddCode({
        dxCode: trimmedCode,
        priority,
        source: newType,
      });
      setNewDxCode("");
      setNewPriority("");
      setNewType([]);
      setShowAddCodeSection(false);
    }
  }, [newDxCode, newPriority, newType, onAddCode]);

  const canAddCode = Boolean(newDxCode.trim()) && newType.length > 0;

  if (!showAddCodeSection) {
    return (
      <>
        <div className="mb-3 flex items-center gap-2">
          <button
            className="edit-dx-code-panel__add-codes-button flex cursor-pointer items-center gap-2 border-none p-0"
            onClick={handleAddCodeClick}
          >
            <PlusCircle width={24} height={24} stroke="#0056D6" />
            <span className="edit-dx-code-panel__add-text font-['Lato'] text-sm font-bold not-italic leading-[1.375rem] text-[#0056D6]">
              Add Codes
            </span>
          </button>
        </div>
        <div className="relative">
          <SearchBar
            placeHolder="Search by DX code, Priority, or Type"
            onSearchClick={onSearchChange}
            id="dx-code-search"
            enableSearchOnChange={true}
            defaultValue={searchTerm}
          />
        </div>
      </>
    );
  }

  return (
    <div className="flex flex-col gap-4 rounded-md border bg-[#F7F9FA] px-3 py-4">
      <div className="flex gap-2">
        <div className="w-1/2">
          <label className="mb-2 block font-['Lato'] text-sm font-normal">
            DX Code
          </label>
          <TextInput
            id="new-dx-code"
            label=""
            hideLabel={true}
            placeholder="DX Code"
            onChange={(data) => setNewDxCode(data.value)}
            defaultValue={newDxCode}
          />
        </div>
        <div className="w-1/2">
          <label className="mb-2 block font-['Lato'] text-sm font-normal">
            Priority
          </label>
          <TextInput
            id="new-priority"
            label=""
            hideLabel={true}
            placeholder="Priority"
            onChange={(data) => setNewPriority(data.value)}
            defaultValue={newPriority}
            type="text"
          />
        </div>
      </div>
      <div>
        <label className="mb-2 block font-['Lato'] text-sm font-normal">
          Type
        </label>
        <DxCodeRowTagFilterBar
          options={DX_CODE_ROW_TYPE_OPTIONS}
          value={newType}
          onChange={setNewType}
          ariaLabel="New DX code type"
        />
      </div>

      <div>
        <button
          className="flex cursor-pointer items-center gap-2 border-none bg-transparent p-0"
          onClick={handleAddCode}
          disabled={!canAddCode}
        >
          <PlusCircle width={24} height={24} stroke="#0056D6" />
          <span className="text-sm font-bold not-italic leading-[1.375rem] text-[#0056D6]">
            Add Code
          </span>
        </button>
      </div>
      <div className="edit-dx-code-panel__footer flex gap-3">
        <Button
          buttonType="secondary"
          size="medium"
          onClick={handleCancelAddCode}
          disabled={false}
        >
          Cancel
        </Button>
        <Button
          buttonType="primary"
          size="medium"
          onClick={handleAddCode}
          disabled={!canAddCode}
        >
          Add Code
        </Button>
      </div>
    </div>
  );
};

export default AddCodeSection;
